(() => {
  'use strict';

  const config = window.CAAPPA || {};
  const root = config.root || '/';

  function formatMoney(cents) {
    const value = (Number(cents) || 0) / 100;
    const currency =
      window.Shopify && Shopify.currency && Shopify.currency.active
        ? Shopify.currency.active
        : 'USD';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency
      }).format(value);
    } catch (error) {
      return `$${value.toFixed(2)}`;
    }
  }

  // ---------------------------------------------------------------------------
  // Mobile navigation
  // ---------------------------------------------------------------------------

  const menuButton = document.querySelector('[data-mobile-menu-button]');
  const mobileNavigation = document.querySelector('[data-mobile-navigation]');

  if (menuButton && mobileNavigation) {
    menuButton.addEventListener('click', () => {
      const isOpen = mobileNavigation.classList.toggle('is-open');

      menuButton.classList.toggle('is-open', isOpen);
      menuButton.setAttribute('aria-expanded', String(isOpen));
    });
  }

  // ---------------------------------------------------------------------------
  // FAQ accordions
  // ---------------------------------------------------------------------------

  document.querySelectorAll('[data-accordion-button]').forEach((button) => {
    button.addEventListener('click', () => {
      const item = button.closest('.accordion-item');
      if (!item) return;

      const isOpen = item.classList.toggle('is-open');
      button.setAttribute('aria-expanded', String(isOpen));

      const mark = button.querySelector('span');
      if (mark) {
        mark.textContent = isOpen ? '−' : '+';
      }
    });
  });

  // ---------------------------------------------------------------------------
  // Product gallery
  // Shopify controls image order. The theme simply follows that order.
  // ---------------------------------------------------------------------------

  document.querySelectorAll('[data-gallery-root]').forEach((gallery) => {
    const mainImage = gallery.querySelector('[data-main-image]');
    const thumbnails = [...gallery.querySelectorAll('[data-thumb]')];

    if (!mainImage || thumbnails.length === 0) return;

    let index = 0;

    function setImage(nextIndex) {
      index = (nextIndex + thumbnails.length) % thumbnails.length;

      const thumbnail = thumbnails[index];
      mainImage.src = thumbnail.dataset.image;
      mainImage.alt = thumbnail.dataset.alt || '';

      thumbnails.forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex === index);
      });
    }

    thumbnails.forEach((thumbnail, thumbnailIndex) => {
      thumbnail.addEventListener('click', () => setImage(thumbnailIndex));
    });

    gallery
      .querySelector('[data-gallery-previous]')
      ?.addEventListener('click', () => setImage(index - 1));

    gallery
      .querySelector('[data-gallery-next]')
      ?.addEventListener('click', () => setImage(index + 1));

    let pointerStart = null;

    gallery.addEventListener('pointerdown', (event) => {
      pointerStart = event.clientX;
    });

    gallery.addEventListener('pointerup', (event) => {
      if (pointerStart === null) return;

      const distance = event.clientX - pointerStart;
      if (Math.abs(distance) > 45) {
        setImage(index + (distance < 0 ? 1 : -1));
      }

      pointerStart = null;
    });
  });

  // ---------------------------------------------------------------------------
  // Cart drawer
  // ---------------------------------------------------------------------------

  const cartOverlay = document.querySelector('[data-cart-overlay]');
  const cartItems = document.querySelector('[data-cart-items]');
  const cartSubtotal = document.querySelector('[data-cart-subtotal]');

  function setCartCount(count) {
    document.querySelectorAll('[data-cart-count], .cart-count').forEach((element) => {
      element.textContent = String(count || 0);
    });
  }

  async function getCart() {
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error('Cart could not be loaded.');
    }

    return response.json();
  }

  function escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function renderCart(cart) {
    setCartCount(cart.item_count);

    if (cartSubtotal) {
      cartSubtotal.textContent = formatMoney(cart.total_price);
    }

    if (!cartItems) return;

    if (cart.items.length === 0) {
      cartItems.innerHTML = `
        <div class="cart-empty">
          <p>Your bag is currently empty.</p>
          <a class="text-link" href="${root}collections/all">Explore the collection →</a>
        </div>
      `;
      return;
    }

    cartItems.innerHTML = cart.items
      .map((item) => {
        const image = item.image
          ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}">`
          : '';

        const variantTitle =
          item.variant_title && item.variant_title !== 'Default Title'
            ? `<p>${escapeHtml(item.variant_title)}</p>`
            : '';

        return `
          <article class="cart-drawer-item">
            ${image}
            <div>
              <h3><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a></h3>
              ${variantTitle}
              <div class="cart-drawer-qty">
                <button
                  type="button"
                  data-cart-change="${escapeHtml(item.key)}"
                  data-qty="${Math.max(0, item.quantity - 1)}"
                  aria-label="Decrease quantity"
                >−</button>
                <span>${item.quantity}</span>
                <button
                  type="button"
                  data-cart-change="${escapeHtml(item.key)}"
                  data-qty="${item.quantity + 1}"
                  aria-label="Increase quantity"
                >+</button>
              </div>
              <button
                class="cart-drawer-remove"
                type="button"
                data-cart-change="${escapeHtml(item.key)}"
                data-qty="0"
              >Remove</button>
            </div>
            <strong>${formatMoney(item.final_line_price)}</strong>
          </article>
        `;
      })
      .join('');
  }

  async function refreshCart() {
    try {
      const cart = await getCart();
      renderCart(cart);
      return cart;
    } catch (error) {
      if (cartItems) {
        cartItems.innerHTML = `
          <div class="cart-empty">
            <p>${escapeHtml(error.message)}</p>
          </div>
        `;
      }

      return null;
    }
  }

  function openCart() {
    if (!cartOverlay) return;

    cartOverlay.classList.add('is-open');
    cartOverlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    refreshCart();
  }

  function closeCart() {
    if (!cartOverlay) return;

    cartOverlay.classList.remove('is-open');
    cartOverlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  document.querySelectorAll('[data-cart-open]').forEach((button) => {
    button.addEventListener('click', openCart);
  });

  document.querySelector('[data-cart-close]')?.addEventListener('click', closeCart);

  cartOverlay?.addEventListener('click', (event) => {
    if (event.target === cartOverlay) {
      closeCart();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeCart();
    }
  });

  cartItems?.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-change]');
    if (!button) return;

    button.disabled = true;

    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: button.dataset.cartChange,
          quantity: Number(button.dataset.qty)
        }),
        credentials: 'same-origin'
      });

      const cart = await response.json();

      if (!response.ok) {
        throw new Error(cart.description || 'Could not update bag.');
      }

      renderCart(cart);
    } catch (error) {
      console.error(error);
      await refreshCart();
    }
  });

  // ---------------------------------------------------------------------------
  // Add-to-bag confirmation toast
  // ---------------------------------------------------------------------------

  const cartToast = document.querySelector('[data-cart-toast]');
  let toastTimer;

  function showToast(message, isError = false) {
    if (!cartToast) return;

    cartToast.querySelector('[data-cart-toast-title]').textContent = isError
      ? 'Could not add item'
      : 'Added to bag';

    cartToast.querySelector('[data-cart-toast-message]').textContent =
      message || 'You can keep shopping.';

    cartToast.classList.add('is-visible');
    clearTimeout(toastTimer);

    toastTimer = setTimeout(
      () => cartToast.classList.remove('is-visible'),
      isError ? 5000 : 3200
    );
  }

  document.querySelector('[data-cart-toast-close]')?.addEventListener('click', () => {
    cartToast?.classList.remove('is-visible');
  });

  document.querySelector('[data-cart-toast-view]')?.addEventListener('click', () => {
    cartToast?.classList.remove('is-visible');
    openCart();
  });

  // ---------------------------------------------------------------------------
  // Product options and real Shopify variant IDs
  // ---------------------------------------------------------------------------

  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const variantsElement = form.querySelector('[data-product-variants]');
    const variantIdInput = form.querySelector('[data-variant-id]');
    const addButton = form.querySelector('[data-add-to-bag]');
    const buyButton = form.querySelector('[data-buy-now]');
    const price = document.querySelector('[data-product-price]');
    const skuElements = document.querySelectorAll(
      '[data-product-sku], [data-product-sku-copy]'
    );
    const optionGroups = [...form.querySelectorAll('[data-option-group]')];

    let variants = [];

    try {
      variants = JSON.parse(variantsElement?.textContent || '[]');
    } catch (error) {
      console.error('Could not parse Shopify product variants.', error);
    }

    function selectedOptions() {
      return optionGroups.map((group) => {
        return group.querySelector('[data-option-value].is-active')?.dataset.optionValue || '';
      });
    }

    function updateVariant() {
      const selected = selectedOptions();
      const variant = variants.find((candidate) => {
        return (
          candidate.options &&
          candidate.options.every((option, optionIndex) => option === selected[optionIndex])
        );
      });

      if (!variant) return;

      if (variantIdInput) {
        variantIdInput.value = variant.id;
      }

      if (price) {
        price.textContent = formatMoney(variant.price);
      }

      skuElements.forEach((element) => {
        element.textContent = variant.sku || '—';
      });

      [addButton, buyButton].forEach((button) => {
        if (button) {
          button.disabled = !variant.available;
        }
      });

      if (addButton) {
        addButton.textContent = variant.available ? 'Add to Bag' : 'Sold Out';
      }
    }

    optionGroups.forEach((group) => {
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        button.addEventListener('click', () => {
          group.querySelectorAll('[data-option-value]').forEach((optionButton) => {
            optionButton.classList.toggle('is-active', optionButton === button);
          });

          const selectedLabel = group.querySelector('[data-selected-option]');
          if (selectedLabel) {
            selectedLabel.textContent = button.dataset.optionValue;
          }

          updateVariant();
        });
      });
    });

    async function addCurrentVariant(goToCheckout = false) {
      if (!variantIdInput?.value) return;

      const button = goToCheckout ? buyButton : addButton;
      if (button?.disabled) return;

      const originalText = button?.textContent;

      if (button) {
        button.disabled = true;
        button.textContent = goToCheckout ? 'Preparing…' : 'Adding…';
      }

      try {
        const formData = new FormData();
        formData.set('id', variantIdInput.value);
        formData.set('quantity', '1');

        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData,
          credentials: 'same-origin'
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || data.message || 'This item could not be added.');
        }

        const cart = await getCart();
        renderCart(cart);

        if (goToCheckout) {
          window.location.href = config.checkoutUrl || '/checkout';
          return;
        }

        showToast('Item added. Keep shopping or open your bag when you are ready.');

        if (button) {
          button.textContent = 'Added ✓';
        }

        setTimeout(() => {
          if (button) {
            button.textContent = originalText;
            button.disabled = false;
          }
        }, 1200);
      } catch (error) {
        showToast(error.message, true);

        if (button) {
          button.textContent = originalText;
          button.disabled = false;
        }
      }
    }

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      addCurrentVariant(false);
    });

    buyButton?.addEventListener('click', () => addCurrentVariant(true));

    updateVariant();
  });

  // ---------------------------------------------------------------------------
  // Lightweight collection filters
  // ---------------------------------------------------------------------------

  const shopGrid = document.querySelector('[data-shop-grid]');

  if (shopGrid) {
    const productCards = [...shopGrid.querySelectorAll('[data-product-card]')];
    const filterButtons = [...document.querySelectorAll('[data-shop-filter]')];
    const heading = document.querySelector('[data-shop-heading]');
    const validFilters = ['all', 'shawls', 'scarves-stoles', 'knitwear'];
    const params = new URLSearchParams(window.location.search);

    let activeFilter = params.get('category') || 'all';
    if (!validFilters.includes(activeFilter)) {
      activeFilter = 'all';
    }

    function filterProducts(category) {
      activeFilter = category;

      productCards.forEach((card) => {
        card.hidden =
          category !== 'all' && card.dataset.productCategory !== category;
      });

      filterButtons.forEach((button) => {
        button.classList.toggle('is-active', button.dataset.shopFilter === category);
      });

      if (heading) {
        const headings = {
          all: 'Shop CAAPPA',
          shawls: 'Shawls',
          'scarves-stoles': 'Scarves & Stoles',
          knitwear: 'Knitwear'
        };

        heading.textContent = headings[category] || 'Shop CAAPPA';
      }
    }

    filterButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const category = button.dataset.shopFilter;
        filterProducts(category);

        const url = new URL(window.location.href);

        if (category === 'all') {
          url.searchParams.delete('category');
        } else {
          url.searchParams.set('category', category);
        }

        window.history.replaceState({}, '', url);
      });
    });

    filterProducts(activeFilter);
  }

  refreshCart();
})();
